import { Component, DestroyRef, OnInit } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { PhysicalInventoryModel } from '../models/physical-inventory.model';
import { isDefined, ModalClass, YesNoModalDialogComponent, YesNoModalDialogModel } from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import { PhysicalInventoryService } from '../services/physical-inventory.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { PhysicalInventoryStorageService } from '../services/physical-inventory-storage.service';

@Component({
    selector: 'app-physical-inventory',
    templateUrl: './physical-inventory.component.html',
    standalone: false,
})
export class PhysicalInventoryComponent implements OnInit {
    public physicalInventoryForm: FormGroup;
    public physicalInventoryFormChildren: FormArray;
    public model: PhysicalInventoryModel;
    public isLoading = false;
    public headingStrings = {
        measurementTools: <string>this.translate.instant('PHYSICAL_INVENTORY_TAB_TITLE_MEASUREMENT_TOOLS'),
        spareParts: <string>this.translate.instant('PHYSICAL_INVENTORY_TAB_TITLE_MATERIALS'),
    };

    public constructor(
        public readonly modalInstance: BsModalRef,
        private modalService: BsModalService,
        private physicalInventoryService: PhysicalInventoryService,
        private readonly formBuilder: FormBuilder,
        private readonly translate: TranslateService,
        private readonly destroyRef: DestroyRef,
        private physicalInventoryStorageService: PhysicalInventoryStorageService,
    ) {}

    public ngOnInit(): void {
        this.physicalInventoryFormChildren = this.formBuilder.array([]);
        this.physicalInventoryForm = this.formBuilder.group({
            children: this.physicalInventoryFormChildren,
        });
    }

    public closeModal(): void {
        this.physicalInventoryStorageService
            .getPIScheduleDetails()
            .pipe(
                take(1),
                filter(isDefined),
                switchMap((piSchedule) => {
                    if (piSchedule.isPISubmitted) {
                        return this.physicalInventoryService.savePIStatusOnSubmit(piSchedule).pipe(map(() => true));
                    }
                    return of(false);
                }),
            )
            .subscribe((isPISubmittedRemotely) => {
                if (isPISubmittedRemotely) {
                    this.modalInstance.hide();
                    return;
                }
                const confirmCloseModalInstance = this.modalService.show(YesNoModalDialogComponent, {
                    backdrop: 'static',
                    class: ModalClass.MODAL_MEDIUM,
                    initialState: {
                        model: new YesNoModalDialogModel(
                            <string>this.translate.instant('PHYSICAL_INVENTORY_CLOSE_LABEL'),
                            <string>this.translate.instant('PHYSICAL_INVENTORY_CLOSE_MESSAGE', {
                                message1: <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_COMMON_MESSAGE'),
                            }),
                            <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_ALERT_CONFIRM_LABEL'),
                            <string>this.translate.instant('PHYSICAL_INVENTORY_SCHEDULE_ALERT_CANCEL_LABEL'),
                        ),
                    },
                });
                confirmCloseModalInstance.content?.modalResult
                    .pipe(
                        filter(Boolean),
                        switchMap(() => this.physicalInventoryStorageService.savePIData(this.model)),
                        tap(() => this.modalInstance.hide()),
                        takeUntilDestroyed(this.destroyRef),
                    )
                    .subscribe();
            });
    }

    public submit(): void {
        this.isLoading = true;
        this.physicalInventoryService
            .submitPhysicalInventory(this.model)
            .pipe(take(1))
            .subscribe(() => {
                this.isLoading = false;
                this.modalInstance.hide();
            });
    }
}
